import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * A custom element that manages a variant picker (custom version).
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class CustomVariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("change", this.variantChanged.bind(this));

    // --- Actualizar galería al cargar según la variante seleccionada por defecto ---
    const hiddenInput = this.querySelector('input[type="hidden"][name="id"]');
    const defaultVariantId = hiddenInput ? hiddenInput.value : this.dataset.defaultVariantId;
    if (defaultVariantId) {
      const radio = this.querySelector(`input[type="radio"][data-variant-id="${defaultVariantId}"]`);
      if (radio) {
        // Construye la URL y actualiza la galería igual que en variantChanged
        const requestUrl = this.buildRequestUrl(radio);
        const pdpUrl = getPdpUrlFromRequestUrl(requestUrl);
        const productId = this.dataset.productId;
        if (typeof productId === "string") {
          applyLoadingState(productId);
          fetchPdpGalleryImages(pdpUrl)
            .then((images) => {
              updateGallerySlideshow(images, productId);
              clearLoadingState(productId);
            })
            .catch((e) => {
              console.warn("[CustomVariantPicker] Error extrayendo imágenes PDP", e);
            })
            .finally(() => {
              clearLoadingState(productId);
            });
        }
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    this.updateSelectedOption(event.target);
    this.dispatchEvent(
      new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? "" })
    );
    // Actualiza la URL SIEMPRE que cambie la variante, sin importar el contexto
    const loadsNewProduct = Boolean(event.target.dataset.connectedProductUrl);
    let newUrl = event.target.dataset.connectedProductUrl;
    const requestUrl = this.buildRequestUrl(event.target);
    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    // --- Sincroniza el precio si no se actualiza automáticamente ---
    fetch(requestUrl, { credentials: 'same-origin' })
      .then(r => r.text())
      .then(htmlText => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        // Busca todos los nuevos bloques de precio para este producto
        const newPrices = Array.from(doc.querySelectorAll(`product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"]`));
        // Busca SOLO el bloque de precio visible dentro de product-card
        const card = this.closest('.product-card');
        if (!card) return;
        const currentPrice = card.querySelector(`product-price[data-product-id="${this.dataset.productId}"] [ref="priceContainer"]`);
        const newPrice = newPrices[0]; // El primero es el principal
        if (currentPrice && newPrice && currentPrice.innerHTML.trim() !== newPrice.innerHTML.trim()) {
          currentPrice.innerHTML = newPrice.innerHTML;
        }
        // Oculta todos los bloques de precio duplicados (del segundo en adelante)
        if (!document.getElementById('hide-duplicate-prices-style')) {
          const style = document.createElement('style');
          style.id = 'hide-duplicate-prices-style';
          style.innerHTML = `
            .product-card product-price[data-product-id] [ref="priceContainer"]:nth-of-type(n+2),
            .product-item product-price[data-product-id] [ref="priceContainer"]:nth-of-type(n+2),
            .card product-price[data-product-id] [ref="priceContainer"]:nth-of-type(n+2) {
              display: none !important;
            }
          `;
          document.head.appendChild(style);
        }
      })
      .catch(() => {/* ignora errores de precio */});

    const pdpUrl = getPdpUrlFromRequestUrl(requestUrl);
    const productId = this.dataset.productId;
    if (typeof productId === "string") {
      applyLoadingState(productId);
      fetchPdpGalleryImages(pdpUrl)
        .then((images) => {
          updateGallerySlideshow(images, productId);
          clearLoadingState(productId);
        })
        .catch((e) => {
          console.warn("[CustomVariantPicker] Error extrayendo imágenes PDP", e);
          clearLoadingState(productId);
        });
    } else {
      console.warn(
        "[CustomVariantPicker] productId is missing in dataset",
        this.dataset
      );
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      );
      if (!targetElement) throw new Error("Target element not found");
      target = targetElement;
    }
    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue
      );
      if (!newSelectedOption) throw new Error("Option not found");
      for (const option of target.options) {
        option.removeAttribute("selected");
      }
      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      "select option[selected], fieldset input:checked"
    );
    if (
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return undefined;
    }
    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) {
      throw new Error("No option value ID found");
    }
    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    );
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error("No option value ID found");
      return optionValueId;
    });
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase()
    );
    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;
      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }
      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    morph(this, newVariantPickerSource);

    // Actualiza el href y el data-featured-media-url del product-card-link
    const newProductCardLink = newHtml.querySelector("product-card-link");
    const currentProductCardLink = this.closest("product-card-link");
    if (newProductCardLink && currentProductCardLink) {
      // Actualiza el href del <a> del card
      const newHref = newProductCardLink
        .querySelector("a")
        ?.getAttribute("href");
      const currentAnchor = currentProductCardLink.querySelector("a");
      if (newHref && currentAnchor) {
        currentAnchor.setAttribute("href", newHref);
      }
      // Actualiza el featured-media-url (como antes)
      const newMediaUrl = newProductCardLink.getAttribute(
        "data-featured-media-url"
      );
      if (newMediaUrl) {
        currentProductCardLink.setAttribute(
          "data-featured-media-url",
          newMediaUrl
        );
      }
    } else {
      console.warn(
        "[CustomVariantPicker] product-card-link not found for media update",
        { newProductCardLink, currentProductCardLink }
      );
    }

    // Actualiza la galería/media visual únicamente con morph (sin lógica de ocultar imágenes)
    const newGallery = newHtml.querySelector(".card-gallery");
    const currentGallery = document.querySelector(
      `.card-gallery[data-product-id="${this.dataset.productId}"]`
    );
    if (newGallery && currentGallery) {
      morph(currentGallery, newGallery);
    } else {
      console.warn("[CustomVariantPicker] No gallery found to morph", {
        newGallery,
        currentGallery,
      });
    }
    return newProduct;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const parser = new DOMParser();
        const newHtml = parser.parseFromString(responseText, "text/html");
        let newProduct;
        if (shouldMorphMain) {
          this.updateMain(newHtml);
        } else {
          newProduct = this.updateVariantPicker(newHtml);
        }
        const textContent = newHtml.querySelector(
          'script[type="application/json"]'
        )?.textContent;
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(
              JSON.parse(textContent),
              this.selectedOptionId,
              {
                html: newHtml,
                productId: this.dataset.productId ?? "",
                newProduct,
              }
            )
          );
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");
    if (!main || !newMain) {
      throw new Error("No new main source found");
    }
    morph(main, newMain);
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl = this.dataset.productUrl;
    const params = [];
    if (selectedOption.dataset.connectedProductUrl) {
      productUrl = selectedOption.dataset.connectedProductUrl;
    }
    if (selectedOption.dataset.variantId) {
      params.push(`variant=${selectedOption.dataset.variantId}`);
    }
    if (selectedOption.dataset.optionValueId) {
      params.push(`option_values=${selectedOption.dataset.optionValueId}`);
    }
    if (
      this.closest("quick-add-component") ||
      this.closest("custom-swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join(
        "&"
      )}`;
    }
    return `${productUrl}?${params.join("&")}`;
  }
}

if (!customElements.get("custom-variant-picker")) {
  customElements.define("custom-variant-picker", CustomVariantPicker);
}

/**
 * Convierte un requestUrl con section_id en la URL limpia del PDP (sin section_id).
 * @param {string} requestUrl
 * @returns {string}
 */
function getPdpUrlFromRequestUrl(requestUrl) {
  const url = new URL(requestUrl, window.location.origin);
  url.searchParams.delete("section_id");
  // Elimina option_values si no lo quieres, si solo quieres variant:
  // url.searchParams.delete('option_values');
  return (
    url.pathname +
    (url.searchParams.toString() ? "?" + url.searchParams.toString() : "")
  );
}

/**
 * Hace fetch al PDP y extrae los src de las imágenes del media-gallery
 * @param {string} pdpUrl
 * @returns {Promise<string[]>}
 */
async function fetchPdpGalleryImages(pdpUrl) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(pdpUrl, { credentials: "same-origin", signal: controller.signal });
    clearTimeout(timeout);
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const gallery = doc.querySelector("media-gallery, .media-gallery");
    if (!gallery) return [];
    const imgs = Array.from(gallery.querySelectorAll("img"));
    return imgs.map((img) =>
      img.src.startsWith("//") ? "https:" + img.src : img.src
    );
  } catch (e) {
    clearTimeout(timeout);
    console.warn('[fetchPdpGalleryImages] Timeout o error en fetch:', e);
    return [];
  }
}

/**
 * Actualiza el slideshow de la galería con nuevas imágenes.
 * @param {string[]} images - URLs de las nuevas imágenes.
 * @param {string} productId - El ID del producto para ubicar la galería correcta.
 */
function clearLoadingState(productId) {
  const gallery = document.querySelector(
    `.card-gallery[data-product-id="${productId}"]`
  );
  if (gallery) {
    gallery.classList.remove('is-updating');
    const card = gallery.closest('.product-card') || gallery.parentElement;
    if (card) {
      card.classList.remove('is-updating');
      if (card instanceof HTMLElement) card.style.pointerEvents = '';
      const blocker = card.querySelector('.gallery-blocker');
      if (blocker) blocker.remove();
    }
    // Rehabilita controles de slideshow
    const arrows = gallery.querySelectorAll('button, [on\\:click]');
    arrows.forEach(btn => {
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    });
  }
}

function applyLoadingState(productId) {
  const gallery = document.querySelector(
    `.card-gallery[data-product-id="${productId}"]`
  );
  if (!gallery) return;
  gallery.classList.add('is-updating');
  const card = gallery.closest('.product-card') || gallery.parentElement;
  if (card) {
    card.classList.add('is-updating');
    if (card instanceof HTMLElement) card.style.pointerEvents = 'none';
    let blocker = card.querySelector('.gallery-blocker');
    if (!blocker) {
      blocker = document.createElement('div');
      blocker.className = 'gallery-blocker';
      blocker.style.position = 'absolute';
      blocker.style.inset = '0';
      blocker.style.background = 'rgba(255,255,255,0.2)';
      blocker.style.zIndex = '9999';
      blocker.style.pointerEvents = 'all';
      blocker.setAttribute('aria-hidden', 'true');
      if (card instanceof HTMLElement && getComputedStyle(card).position === 'static') {
        card.style.position = 'relative';
      }
      card.appendChild(blocker);
    }
  }
  // Deshabilita controles de slideshow
  const arrows = gallery.querySelectorAll('button, [on\\:click]');
  arrows.forEach(btn => {
    if (btn instanceof HTMLButtonElement) btn.disabled = true;
  });
}

function updateGallerySlideshow(images, productId) {
  // Encuentra la galería correcta usando el data-product-id
  const gallery = document.querySelector(
    `.card-gallery[data-product-id="${productId}"]`
  );
  if (!gallery) {
    console.warn(
      "[CustomVariantPicker] No se encontró la galería para actualizar imágenes",
      { productId }
    );
    return;
  }

  // Encuentra el contenedor principal (ajusta el selector si es necesario)
  const card = gallery.closest('.product-card') || gallery.parentElement;

  // Añade clase y overlay para bloquear todo
  gallery.classList.add('is-updating');
  if (card) {
    card.classList.add('is-updating');
    const cardEl = card;
    // Crea overlay si no existe
    let blocker = card.querySelector('.gallery-blocker');
    if (!blocker) {
      blocker = document.createElement('div');
      blocker.className = 'gallery-blocker';
      if (blocker instanceof HTMLElement) {
        blocker.style.position = 'absolute';
        blocker.style.inset = '0';
        blocker.style.background = 'rgba(255,255,255,0.2)';
        blocker.style.zIndex = '9999';
        blocker.style.pointerEvents = 'all';
        blocker.setAttribute('aria-hidden', 'true');
      }
      // Asegura que el contenedor tenga position relative
      if (cardEl && cardEl instanceof HTMLElement) {
        if (getComputedStyle(cardEl).position === 'static') {
          cardEl.style.position = 'relative';
        }
        if (cardEl instanceof HTMLElement) {
          cardEl.appendChild(blocker);
          cardEl.style.pointerEvents = 'none';
        }
      }
    } else {
      if (card && card instanceof HTMLElement) {
        card.style.pointerEvents = 'none';
      }
    }
  }

  // Deshabilita controles de slideshow
  const arrows = gallery.querySelectorAll('button, [on\\:click]');
  arrows.forEach((btn) => {
    if (btn instanceof HTMLButtonElement) btn.disabled = true;
  });

  // Encuentra el slideshow dentro de la galería
  const slideshowSlides = gallery.querySelector("slideshow-slides");
  if (!slideshowSlides) {
    console.warn(
      "[CustomVariantPicker] No se encontró slideshow-slides dentro de la galería",
      { gallery }
    );
    gallery.classList.remove('is-updating');
    if (card) {
      card.classList.remove('is-updating');
      card.style.pointerEvents = '';
      const blocker = card.querySelector('.gallery-blocker');
      if (blocker) blocker.remove();
    }
    arrows.forEach(btn => {
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    });
    return;
  }

  // Limpia los slides existentes
  slideshowSlides.innerHTML = "";
  // Muestra overlay con spinner y texto
  let loadingBlocker = card && card.querySelector('.gallery-blocker');
  if (!loadingBlocker && card && card instanceof HTMLElement) {
    loadingBlocker = document.createElement('div');
    loadingBlocker.className = 'gallery-blocker';
    loadingBlocker.innerHTML = `
      <div class="gallery-blocker__spinner" aria-live="polite" aria-busy="true">
        <span class="gallery-blocker__icon"></span>
      </div>
    `;
    loadingBlocker.style.position = 'absolute';
    loadingBlocker.style.inset = '0';
    loadingBlocker.style.background = 'rgba(255,255,255,0.7)';
    loadingBlocker.style.zIndex = '9999';
    loadingBlocker.style.pointerEvents = 'all';
    loadingBlocker.setAttribute('aria-hidden', 'false');
    if (getComputedStyle(card).position === 'static') {
      card.style.position = 'relative';
    }
    card.appendChild(loadingBlocker);
  } else if (loadingBlocker) {
    loadingBlocker.setAttribute('aria-hidden', 'false');
    loadingBlocker.innerHTML = `
      <div class="gallery-blocker__spinner" aria-live="polite" aria-busy="true">
        <span class="gallery-blocker__icon"></span>
      </div>
    `;
  }

  // Crea y agrega nuevos slides con las imágenes, y espera a que todas carguen
  const loadPromises = images.map((src, idx) => {
    return new Promise((resolve) => {
      const slide = document.createElement("slideshow-slide");
      slide.setAttribute("ref", "slides[]");
      slide.setAttribute("aria-hidden", idx === 0 ? "false" : "true");
      slide.className = "product-media-container media-fit  product-media-container--image";
      slide.style.setProperty('--product-media-fit', 'cover');
      const img = document.createElement('img');
      img.src = src;
      img.className = 'product-media__image';
      //img.alt = 'product.title';
      img.style.width = '100%';
      img.loading = 'lazy';
      img.onload = img.onerror = () => resolve();
      const mediaDiv = document.createElement('div');
      mediaDiv.className = 'product-media';
      mediaDiv.style.setProperty('--ratio', '1.0');
      mediaDiv.appendChild(img);
      slide.appendChild(mediaDiv);
      slideshowSlides.appendChild(slide);
    });
  });

  // Refuerzo: elimina cualquier slide extra que haya quedado por error
  const allSlides = gallery.querySelectorAll('slideshow-slide');
  allSlides.forEach((slide, idx) => {
    if (idx > 0) slide.remove();
  });

  // Timeout de seguridad para limpiar loader/overlay aunque alguna imagen no cargue
  const failSafeTimeout = setTimeout(() => {
    gallery.classList.remove('is-updating');
    if (card) {
      card.classList.remove('is-updating');
      card.style.pointerEvents = '';
      const blocker = card.querySelector('.gallery-blocker');
      if (blocker) blocker.remove();
    }
    arrows.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    });
  }, 3000); // 3 segundos de margen

  Promise.all(loadPromises).then(() => {
    clearTimeout(failSafeTimeout); // Cancela el timeout si todo carga bien
    gallery.classList.remove('is-updating');
    if (card) {
      card.classList.remove('is-updating');
      card.style.pointerEvents = '';
      const blocker = card.querySelector('.gallery-blocker');
      if (blocker) blocker.setAttribute('aria-hidden', 'true');
      setTimeout(() => { if (blocker) blocker.remove(); }, 250); // Breve retardo para suavidad visual
    }
    arrows.forEach((btn) => {
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    });
    gallery.dispatchEvent(new CustomEvent('gallery:updated', { bubbles: true }));
  });

}

// Protección global para listeners de pointerenter/pointerleave en galerías
(function attachGalleryInteractionGuards() {
  document.addEventListener('pointerenter', function(e) {
    const target = e.target;
    if (target && typeof (target.closest) === 'function') {
      const gallery = target.closest('.card-gallery');
      if (gallery && gallery.classList.contains('is-updating')) {
        e.stopImmediatePropagation();
      }
    }
  }, true);
  document.addEventListener('pointerleave', function(e) {
    const target = e.target;
    if (target && typeof (target.closest) === 'function') {
      const gallery = target.closest('.card-gallery');
      if (gallery && gallery.classList.contains('is-updating')) {
        e.stopImmediatePropagation();
      }
    }
  }, true);
})();

// CSS para overlay y cursor de espera
if (!document.getElementById('gallery-blocker-style')) {
  const style = document.createElement('style');
  style.id = 'gallery-blocker-style';
  style.innerHTML = `
    /* Swatch Color Map */
    :root {
      --swatch-bronze: #b08d57;
      --swatch-onyx: #353839;
      --swatch-silver: #bfc1c2;
      --swatch-ruby: #9b111e;
      --swatch-sapphire: #0f52ba;
      --swatch-platinum: #e5e4e2;
    }
    .color-swatch[style*="background-color: bronze"],
    .color-swatch[style*="background-color: Bronze"] {
      background-color: var(--swatch-bronze, #b08d57) !important;
    }
    .color-swatch[style*="background-color: onyx"],
    .color-swatch[style*="background-color: Onyx"] {
      background-color: var(--swatch-onyx, #353839) !important;
    }
    .color-swatch[style*="background-color: silver"],
    .color-swatch[style*="background-color: Silver"] {
      background-color: var(--swatch-silver, #bfc1c2) !important;
    }
    .color-swatch[style*="background-color: ruby"],
    .color-swatch[style*="background-color: Ruby"] {
      background-color: var(--swatch-ruby, #9b111e) !important;
    }
    .color-swatch[style*="background-color: sapphire"],
    .color-swatch[style*="background-color: Sapphire"] {
      background-color: var(--swatch-sapphire, #0f52ba) !important;
    }
    .color-swatch[style*="background-color: platinum"],
    .color-swatch[style*="background-color: Platinum"] {
      background-color: var(--swatch-platinum, #e5e4e2) !important;
    }

    .gallery-blocker {
      position: absolute;
      inset: 0;
      background: rgba(255,255,255,0.7);
      z-index: 9999;
      pointer-events: all;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .product-card.is-updating,
    .card-gallery.is-updating {
      cursor: wait !important;
    }
    .card-gallery.is-updating slideshow-slide,
    .card-gallery.is-updating slideshow-slides {
      pointer-events: none !important;
      cursor: wait !important;
      user-select: none !important;
    }
    .gallery-blocker__spinner {
      display: flex;
      align-items: center;
      gap: 0.75em;
      font-size: 1.1em;
      color: #333;
    }
    .gallery-blocker__icon {
      display: inline-block;
      width: 1.5em;
      height: 1.5em;
      border: 3px solid #bbb;
      border-top: 3px solid #333;
      border-radius: 50%;
      animation: gallery-spin 1s linear infinite;
      margin-right: 0.5em;
    }
    @keyframes gallery-spin {
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

// Protección global para listeners de pointerenter/pointerleave en galerías
(function attachGalleryInteractionGuards() {
  document.addEventListener('pointerenter', function(e) {
    const target = e.target;
    if (target && typeof (target.closest) === 'function') {
      const gallery = target.closest('.card-gallery');
      if (gallery && gallery.classList.contains('is-updating')) {
        e.stopImmediatePropagation();
      }
    }
  }, true);
  document.addEventListener('pointerleave', function(e) {
    const target = e.target;
    if (target && typeof (target.closest) === 'function') {
      const gallery = target.closest('.card-gallery');
      if (gallery && gallery.classList.contains('is-updating')) {
        e.stopImmediatePropagation();
      }
    }
  }, true);
})();
